import { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import type { UserScoreSummary, TrendDirection } from '../../../api/generated';

interface OrgUsersTableProps {
  users: UserScoreSummary[];
}

type SortField = 'display_name' | 'call_count' | 'avg_overall_score' | 'avg_engagement' | 'avg_discovery' | 'avg_presentation' | 'avg_closing';

function TrendChip({ trend }: { trend: TrendDirection }) {
  if (trend === 'improving') {
    return <Chip icon={<TrendingUpIcon />} label="Up" size="small" color="success" variant="outlined" />;
  }
  if (trend === 'declining') {
    return <Chip icon={<TrendingDownIcon />} label="Down" size="small" color="error" variant="outlined" />;
  }
  return <Chip icon={<TrendingFlatIcon />} label="Stable" size="small" variant="outlined" />;
}

function ScoreText({ score }: { score: number | null }) {
  if (score == null) {
    return (
      <Typography variant="body2" sx={{
        color: "text.secondary"
      }}>—</Typography>
    );
  }
  return <Typography variant="body2">{score}</Typography>;
}

export function OrgUsersTable({ users }: OrgUsersTableProps) {
  const [sortField, setSortField] = useState<SortField>('avg_overall_score');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sorted = [...users].sort((a, b) => {
    const aVal = a[sortField] ?? -1;
    const bVal = b[sortField] ?? -1;
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
  });

  if (users.length === 0) {
    return (
      <Typography
        sx={{
          color: "text.secondary",
          py: 4,
          textAlign: 'center'
        }}>No user data available
              </Typography>
    );
  }

  const columns: { field: SortField; label: string; align?: 'right' | 'center' }[] = [
    { field: 'display_name', label: 'Name' },
    { field: 'call_count', label: 'Calls', align: 'right' },
    { field: 'avg_overall_score', label: 'Overall', align: 'right' },
    { field: 'avg_engagement', label: 'Engage', align: 'right' },
    { field: 'avg_discovery', label: 'Discover', align: 'right' },
    { field: 'avg_presentation', label: 'Present', align: 'right' },
    { field: 'avg_closing', label: 'Close', align: 'right' },
  ];

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.field} align={col.align}>
                <TableSortLabel
                  active={sortField === col.field}
                  direction={sortField === col.field ? sortDir : 'desc'}
                  onClick={() => handleSort(col.field)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell>Trend</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((user) => (
            <TableRow key={user.user_id} hover>
              <TableCell>
                <Box>
                  <Typography variant="body2">
                    {user.display_name || user.email}
                  </Typography>
                  {user.display_name && user.email && (
                    <Typography variant="caption" sx={{
                      color: "text.secondary"
                    }}>
                      {user.email}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell align="right">{user.call_count}</TableCell>
              <TableCell align="right">
                <ScoreText score={user.avg_overall_score} />
              </TableCell>
              <TableCell align="right">
                <ScoreText score={user.avg_engagement} />
              </TableCell>
              <TableCell align="right">
                <ScoreText score={user.avg_discovery} />
              </TableCell>
              <TableCell align="right">
                <ScoreText score={user.avg_presentation} />
              </TableCell>
              <TableCell align="right">
                <ScoreText score={user.avg_closing} />
              </TableCell>
              <TableCell>
                <TrendChip trend={user.trend} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
